"use client";

import { Card, CircularProgress, StepLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Step from "@mui/material/Step";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikHelpers } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AsaasLogo from "@/assets/logos/asaas-servicos-financeiros.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { TALK2PAY_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useGetCustomerById from "@/hooks/requests/customer/use-get-customer-by-id";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import useCreateT2Pay from "@/hooks/requests/t2Pay/use-create-T2-pay";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useTabsOrientation from "@/hooks/use-tabs-orientation";
import { ActivationFormData } from "@/interfaces/talk2pay.iterfaces";
import { Talk2PayConnectionMessages } from "@/messages/platforms/talk2PayConnection/talk2Pay-connection.messages";
import { BreadCrumb } from "@/types/page.types";
import { PlatformsName } from "@/types/platform.types";

import { Activation } from "./Activation";
import { Documentation } from "./Documentation";
import { Features } from "./Features";
import Rates from "./Rates";
import { buildInitialData, normalizeActivationFormData, validationSchema } from "./formData";

type Props = {
  messages: Talk2PayConnectionMessages;
};

export function Talk2PayConnection({ messages }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const router = useNavigate();
  const { lang } = useLanguage();
  const { checkPermissions } = useAuth();
  const { create, isLoading } = useCreateT2Pay();
  const hasNoPermission = checkPermissions(TALK2PAY_PERMISSIONS.CREATE);
  const customerId = useGetCustomerId();
  const _id = Number(customerId) || 0;
  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerById({ _id });
  const { data: platformsResponse } = useGetAllPlatforms();
  const orientation = useTabsOrientation();

  const pageTitle = useMemo(() => {
    const platform = platformsResponse?.items?.find(
      (p) => p.name?.toUpperCase() === PlatformsName.ASAAS || p.id === 2
    );

    return platform?.displayName || messages.pageTitle;
  }, [platformsResponse, messages.pageTitle]);

  useEffect(() => {
    const delayCheckPermissions = setTimeout(() => {
      if (hasNoPermission) router(ROUTE_URLS.serviceDashboard(lang));
    }, 3000);

    return () => clearTimeout(delayCheckPermissions);
  }, [hasNoPermission, lang, router]);

  if (hasNoPermission) return null;

  const handleNext = () => {
    if (isLastStep()) return;
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep > 0 ? prevActiveStep - 1 : prevActiveStep));
  };

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.breadCrumbTitle, href: ROUTE_URLS.platform(lang) },
  ];

  const steps = [
    {
      label: messages.featuresStepTitle,
      content: <Features messages={messages} />,
    },
    {
      label: messages.ratesStepTitle,
      content: <Rates />,
    },
    {
      label: messages.activationStepTitle,
      content: <Activation messages={messages} />,
    },
    {
      label: messages.documentationStepTitle,
      content: <Documentation messages={messages} />,
    },
  ];

  const totalSteps = steps.length;

  const isLastStep = () => activeStep === totalSteps - 1;

  const handleSubmit = async (
    values: ActivationFormData,
    actions: FormikHelpers<ActivationFormData>
  ) => {
    const formData = normalizeActivationFormData(values);

    const onSuccess = () => {
      setIsProcessing(true);
      setTimeout(() => {
        setIsProcessing(false);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }, 20000);
    };

    const errorMessage = messages.errorCreateAccount;

    try {
      await create({ formData, onSuccess, errorMessage });
    } finally {
      actions.setSubmitting(false);
    }
  };

  const completeConnection = () => router(ROUTE_URLS.whatsappConnection(lang));

  return (
    <PageLayout title={pageTitle} messages={messages} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <MDBox mt={5} mb={9}>
          <Grid container justifyContent="center">
            <Grid size={10}>
              <Card>
                <MDBox mt={-3} mb={3} mx={2}>
                  <Stepper orientation={orientation} activeStep={activeStep} alternativeLabel>
                    {steps.map((step) => (
                      <Step key={step.label}>
                        <StepLabel>{step.label}</StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </MDBox>
                <MDBox p={2}>
                  <Suspense
                    isSuspended={isLoading || isLoadingCustomer}
                    fallback={<CircularProgress />}
                  >
                    <Formik
                      initialValues={buildInitialData(customer)}
                      validationSchema={validationSchema(messages)}
                      onSubmit={handleSubmit}
                    >
                      {(formik) => (
                        <form onSubmit={formik.handleSubmit}>
                          <MDBox>
                            {isProcessing ? (
                              <MDBox p={2} textAlign="center">
                                <CircularProgress />
                                <MDTypography variant="h6" mt={2}>
                                  {messages.processingData}
                                </MDTypography>
                              </MDBox>
                            ) : (
                              steps[activeStep].content
                            )}

                            <MDBox
                              mt={3}
                              width="100%"
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <img
                                src={AsaasLogo}
                                alt="Serviços financeiros Asaas"
                                style={{ height: 40, objectFit: "contain" }}
                              />

                              {!isProcessing && (
                                <MDBox display="flex" gap={1}>
                                  {activeStep > 0 && (
                                    <MDButton variant="outlined" color="dark" onClick={handleBack}>
                                      {messages.backButton}
                                    </MDButton>
                                  )}

                                  {isLastStep() ? (
                                    <MDButton
                                      variant="gradient"
                                      color="dark"
                                      onClick={completeConnection}
                                    >
                                      {messages.completeButton}
                                    </MDButton>
                                  ) : activeStep === 2 ? (
                                    <MDButton type="submit" variant="gradient" color="dark">
                                      {messages.nextButton}
                                    </MDButton>
                                  ) : (
                                    <MDButton variant="gradient" color="dark" onClick={handleNext}>
                                      {messages.nextButton}
                                    </MDButton>
                                  )}
                                </MDBox>
                              )}
                            </MDBox>
                          </MDBox>
                        </form>
                      )}
                    </Formik>
                  </Suspense>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
}
